import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';

import {
  ChartComponent,
  ApexAxisChartSeries,
  ApexChart,
  ApexFill,
  ApexTooltip,
  ApexXAxis,
  ApexLegend,
  ApexDataLabels,
  ApexTitleSubtitle,
  ApexPlotOptions,
  ApexYAxis,
  ApexGrid,
} from 'ng-apexcharts';
import { Subscription } from 'rxjs';
import { MemoryService } from 'libs/portal/src/services/memory/memory.service';
import { NavigationService } from 'libs/portal/src/services/navigation/navigation.service';
import { ToolsService } from 'libs/portal/src/services/tools/tools.service';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  markers: any;
  stroke: any;
  yaxis: ApexYAxis | ApexYAxis[];
  plotOptions: ApexPlotOptions;
  dataLabels: ApexDataLabels;
  colors: string[];
  labels: string[] | number[];
  title: ApexTitleSubtitle;
  subtitle: ApexTitleSubtitle;
  legend: ApexLegend;
  fill: ApexFill;
  tooltip: ApexTooltip;
  grid?: ApexGrid;
};

declare global {
  interface Window {
    Apex: any;
  }
}

@Component({
  selector: 'app-device-bundle-used-chart',
  templateUrl: './device-bundle-used-chart.component.html',
  styleUrls: ['./device-bundle-used-chart.component.scss'],
})
export class DeviceBundleUsedChartComponent implements OnInit, OnDestroy, OnChanges {
  @Input() data: any;
  @Input() titleText: any;
  @ViewChild('chart') chart: ChartComponent;
  public chartOptions: Partial<ChartOptions>;
  public commonAreaSparlineOptions: Partial<ChartOptions> = {};

  graphLoaded = false;
  sidenavObservable: Subscription;
  private unit: string;
  memorySub: Subscription;

  constructor(private tools: ToolsService, private navigationService: NavigationService, private memoryService: MemoryService) {}

  ngOnInit() {
    this.initDataConversionHandler();
    this.buildChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.buildChart();
  }

  buildChart(): void {
    const tempSeries: any = [];
    if (this.data != null && this.data.totalData != null && this.data.totalData.length != 0) {
      tempSeries.push({
        name: `Bundle (${this.unit})`,
        data:
          this.data != null
            ? this.data.totalData.map((total) => {
                total = this.tools.ConvertDataFromBytes(total, this.unit);
                if (total != null) {
                  return total.toFixed(1);
                }
              })
            : [],
        color: '#C93F55',
      });
    }

    if (this.data != null && this.data.projected != null && this.data.projected.length != 0) {
      tempSeries.push({
        name: `Projection (${this.unit})`,
        data:
          this.data != null
            ? this.data.projected.map((projected) => {
                projected = this.tools.ConvertDataFromBytes(projected, this.unit);
                if (projected != null) {
                  return projected.toFixed(1);
                }
              })
            : [],
        color: '#FFB347',
      });
    }

    if (this.data != null && this.data.dataUsed != null && this.data.dataUsed.length != 0) {
      tempSeries.push({
        name: `Used (${this.unit})`,
        data:
          this.data != null
            ? this.data.dataUsed.map((dataUsed) => {
                dataUsed = this.tools.ConvertDataFromBytes(dataUsed, this.unit);
                if (dataUsed != null) {
                  return dataUsed.toFixed(1);
                }
              })
            : [],
        color: '#3FC944',
      });
    }
    let thisMonth = new Date();
    const tempStart = this.data.NewDate != null ? this.data.NewDate : new Date();
    let tempEnd = this.data.NewDate != null ? this.tools.GetEndOfMonthFromDate(this.data.NewDate) : new Date();
    tempEnd = this.tools.GetStartOfMonth(tempStart).toDateString() == this.tools.GetStartOfMonth(thisMonth).toDateString() ? thisMonth : tempEnd;
    this.chartOptions = {
      series: tempSeries,
      title: {
        // sv-SE because yyyy/MM/dd
        text:
          this.titleText != null
            ? this.titleText
            : `Bundle used in ${this.unit} (` + this.tools.GetStartOfMonth(tempStart).toLocaleDateString('sv-SE') + ' - ' + tempEnd.toLocaleDateString('sv-SE') + ')',
        align: 'left',
        style: {
          fontSize: '24px',
          color: '#08074B',
          fontWeight: '400',
        },
      },
      chart: {
        id: 'chart2',
        type: 'line',
        height: 300,
        dropShadow: {
          enabled: true,
          color: '#000',
          top: 18,
          left: 7,
          blur: 10,
          opacity: 0.2,
        },
      },
      grid: {
        yaxis: {
          lines: {
            show: false,
          },
        },
        xaxis: {
          lines: {
            show: true,
          },
        },
      },
      dataLabels: {
        enabled: true,
      },
      stroke: {
        curve: 'smooth',
        width: 4,
      },
      xaxis: {
        type: 'category',
        categories: this.data != null ? this.data.weeks.map((week) => week) : [],
        axisBorder: {
          show: true,
        },
        axisTicks: {
          show: true,
        },
      },
      tooltip: {
        fixed: {
          enabled: false,
        },
        x: {
          show: false,
        },
        marker: {
          show: false,
        },
      },
    };

    this.graphLoaded = true;

    this.sidenavObservable = this.navigationService.GetSideNav(() => {
      if (this.graphLoaded != false) {
        const temp = this.graphLoaded;
        this.graphLoaded = false;
        setTimeout(() => {
          this.graphLoaded = temp;
        });
      }
    });
  }

  initDataConversionHandler(): void {
    this.unit = this.memoryService.getLocalPreferences().getValue().preferedGraphUnit;
    this.memorySub = this.memoryService.getLocalPreferences().subscribe((prefs) => {
      if (this.unit != prefs.preferedGraphUnit) {
        this.unit = prefs.preferedGraphUnit;
        this.buildChart();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.sidenavObservable != null) {
      this.sidenavObservable.unsubscribe();
    }
    this.memorySub?.unsubscribe();
  }
}
